import { DIContainer } from "Global/Base/DIContainer";
import { EventCallback } from "Global/Base/Events";
import { IDisposable } from "Global/Base/IDisposable";
import { IRole, IRoles } from "OW.Remoting/Remoting/IRolesAndICompetences";
import { IUserSettingsManager } from "OW.Remoting/Remoting/IUserSettingsManager";
import { ArrayDataSource, ArrayMSLOVDataSource } from "UI/UI.Controls/DataSource/ArrayDataSource";
import { Registrator } from "UI/UI/UI/Controls/Registrator";
import { OrderType } from "../../../../Global/Base/DataSources/Interfaces";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { ModelDescriptionsManager } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { Column } from "../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { ColumnFactory } from "../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Caption } from "../../UI/Controls/Caption";
import { IContentBuilder } from "../../UI/DataBuilders/IContentBuilder";
import { MessageButtonType, MessageWindow } from "../../UI/MessageWindow";
import { UIManager } from "../../UI/UIManager";
import { FrameSharedMethods } from "../../UI/XGControls/Frames/FrameSharedMethods";
import { UserActionLogger } from "../UserActionLogger";
import { MultiSelectList } from "./MultiSelectList";
import { PasswordSectionHandler } from "./PasswordSectionHandler";
import { SectionHandler } from "./SectionHandler";

export class AppSettingsContainer extends HTMLElement implements IDisposable {
    private _sections: SectionHandler[];
    private _openerWindow?: MessageWindow;
    private _okButton: HTMLElement;
    private _closeButton: HTMLElement;
    private _rolesDropdown: Element;

    private _selectedRoleId: string;
    // @ts-ignore // nečteno, ale ponecháno
    private _rolesSection: MultiSelectList;
    // @ts-ignore // nečteno, ale ponecháno
    private _competencesSection: MultiSelectList;

    private _userSettingsManager: IUserSettingsManager;

    private _okButtonClickListener: EventListener;
    private _closeButtonClickListener: EventListener;
    private _rolesDropDownChangeListener: EventListener;
    private _rolesSelectionChangedListener: EventCallback;

    public set openerWindow(m: MessageWindow) {
        this._openerWindow = m;
    }
    public get openerWindow(): MessageWindow {
        return this._openerWindow;
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        Caption.createCaptionsForElements(this); // vytvoří label elementy ke komponentám, obdobně jak to dělá AlignPanel
        this._userSettingsManager = DIContainer.instance.create(IUserSettingsManager);
        Registrator.initializeControls(this);
        this._registerListeners();
        this._registerSections();
    }

    private _registerListeners(): void {
        UserActionLogger.registerWindow(window);
        this._okButton = this.parentElement.querySelector("[data-id='ok-button']");
        if (this._okButton) {
            this._okButton.classList.add("Disabled");
            this._okButton.addEventListener("click", this._okButtonClickListener = async () => {
                if (this._okButton.classList.contains("Disabled"))
                    return;

                UIManager.instance.showLoader();
                try {
                    if (!(await this.save()))
                        return;
                } finally {
                    UIManager.instance.hideLoader();
                }

                //if (relogRecommended)
                // TODO: až se udělají toast notifikace, tak se zobrazí zpráva s tím, že pro aplikování změn je potřeba se odhlásit
                // TODO: a i u obyčejného úspěšného průběhu by se to uživateli mělo nějak zobrazit

                if (this._openerWindow)
                    this._openerWindow.close(MessageButtonType.ok);
            });
        }

        this._closeButton = this.parentElement.querySelector("[data-id='close-button']");
        if (this._closeButton)
            this._closeButton.addEventListener("click", this._closeButtonClickListener = () => {
                if (this._openerWindow)
                    this._openerWindow.close(MessageButtonType.cancel);
                this.dispose();
            });
    }

    /**
     * Pro každou oblast stránky s vlastním objektem je nadefinován SectionHandler, který ji obsluhuje
     */
    private async _registerSections(): Promise<void> {
        UIManager.instance.showLoader();
        try {
            const userProfileRoles = await this._userSettingsManager.getRoles();
            const userRoles: IRole[] = Object.values(userProfileRoles);
            const userProfileRolesSorted = this._sortSettingItemsInfo(userRoles);
            const rolesSectionDataSource = this._createDataSource(userProfileRolesSorted);
            const rolesIds = userRoles.filter(userRole => userRole.active).map(activeUserRole => activeUserRole.id);
            this._sections = [];
            const rolesSectionEl = this.querySelector("#roles-container");
            this._rolesSection = new MultiSelectList(
                rolesSectionEl, rolesSectionDataSource, rolesSectionEl.getAttribute("data-npattrname"),
                await this._createColumns(rolesSectionEl.getAttribute("data-displayedclass"), rolesSectionEl as HTMLElement),
                [{ attributeName: rolesSectionEl.getAttribute("data-sort"), order: OrderType.ascending }],
            );
            this._rolesSection.selectionChanged.add(this._rolesSelectionChangedListener = (_sender, prm) => {
                this._userSettingsManager.updateRoleActivity(prm.id, prm.value);
                this._okButton.classList.toggle("Disabled", false);
            });

            this._rolesDropdown = this._createRolesDropdown(this.querySelector("#competence-role"), userProfileRoles);
            const nameAttr = this.querySelector("#comp-name-attr")?.getAttribute("data-attrname");
            const codeAttr = this.querySelector("#comp-code-attr")?.getAttribute("data-attrname");

            this._rolesDropdown.addEventListener("change", this._rolesDropDownChangeListener = async (event: any) => {
                this._selectedRoleId = event.target.value;
                this._competencesSection.dataSource.init(this._sortSettingItemsInfo(userProfileRoles[event.target.value]?.competenceRanges), "id", [{ originalName: "name", newName: nameAttr }, { originalName: "code", newName: codeAttr }]);
                await this._competencesSection.dataSource.loadNewData(false);
            });

            const competencesSectionDataSource = new ArrayMSLOVDataSource();

            this._selectedRoleId = (this._rolesDropdown as HTMLSelectElement).value;
            competencesSectionDataSource.init(this._sortSettingItemsInfo(userProfileRoles[this._selectedRoleId]?.competenceRanges), "id", [{ originalName: "name", newName: nameAttr }, { originalName: "code", newName: codeAttr }]);

            const competencesSectionEl = this.querySelector("#competences-container");
            this._competencesSection = new MultiSelectList(
                competencesSectionEl, competencesSectionDataSource, competencesSectionEl.getAttribute("data-npattrname"),
                await this._createColumns(competencesSectionEl.getAttribute("data-displayedclass"), competencesSectionEl as HTMLElement),
                [{ attributeName: competencesSectionEl.getAttribute("data-sort"), order: OrderType.ascending }],
            );

            this._competencesSection.selectionChanged.add((_sender, prm) => {
                this._userSettingsManager.updateCompetenceActivity(this._selectedRoleId, prm.id, prm.value);
                this._okButton.classList.toggle("Disabled", false);
            });

            const passwordSectionEl = this.querySelector("#password-change-container");
            this._sections.push(new PasswordSectionHandler(
                passwordSectionEl,
                () => OWManager.newObject(passwordSectionEl.getAttribute("data-objectname")),
                (obj) => AppSettingsContainer._runObjectMethod(obj, passwordSectionEl.getAttribute("data-savemethod")),
                (content) => AppSettingsContainer._showInlineMessage(passwordSectionEl.querySelector(".errors"), content),
                "current-password", "new-password1", "new-password2", rolesIds,
            ));

            this._sections.forEach(s => s.changed.add(() => this._okButton.classList.toggle("Disabled", !this._sections.some(s => s.isChanged))));
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private _createRolesDropdown = (rootElement: HTMLElement, roles: IRoles): HTMLElement => {
        for (const role of Object.values(roles).sort((a, b) => (a.name.localeCompare(b.name)))) {
            const option = createElement({ type: "option", value: role.id, html: StringUtils.stringToHTMLText(role.name) });
            rootElement.appendChild(option);
        }
        return rootElement;
    };

    private static async _showInlineMessage(container: Element, content: IContentBuilder | null): Promise<void> {
        if (!content) {
            container.innerHTML = "";
            return;
        }

        container.innerHTML = (await content.build()).outerHTML; // zabrání problikávání při změně
    }

    private static async _runObjectMethod(obj: OWObject, methodName: string): Promise<void> {
        const result = await obj.run({ methodName });
        if (result)
            await UIManager.instance.showResultMessages(result.messages);
    }

    private async _createColumns(objectName: string, element: HTMLElement): Promise<Column[]> {
        const columnsDefinition = FrameSharedMethods.getColumnsDefinition(element);
        const attrNames = columnsDefinition.map((column) => column.attrName);
        const attrInfo = await ModelDescriptionsManager.instance.getAttributesInfo(objectName, attrNames);

        const roleColumns: Column[] = [];
        for (const colDef of columnsDefinition) {
            const ai = attrInfo.find(i => StringUtils.compareCaseInsensitive(i.attributeName, colDef.attrName));
            const col = ColumnFactory.initColumn(colDef, ai.type, ai);
            roleColumns.push(col);
        }

        return roleColumns;
    }

    public async save(): Promise<boolean> {
        return (await Promise.all([...this._sections.map(s => s.save()), this._userSettingsManager.saveData().then(() => true)])).every(succeeded => succeeded);
    }

    /**
     * Vytvoření datového zdroje pro sekci uživatlských rolí.
     * @param roles Vstupní data; seznam přidělených rolí.
     */
    protected _createDataSource<T extends object>(roles: IRole[]): ArrayDataSource<T> {
        const dataSource = new ArrayMSLOVDataSource();
        const nameAttr = this.querySelector("#role-name-attr")?.getAttribute("data-attrname");
        const codeAttr = this.querySelector("#role-code-attr")?.getAttribute("data-attrname");
        dataSource.init(roles, "id", [{ originalName: "name", newName: nameAttr }, { originalName: "code", newName: codeAttr }, { originalName: "implicit", newName: "disabled" }]);
        return dataSource as ArrayDataSource<T>;
    }

    /**
     * Funkce vrací HTML prvek reprezentující tuto tříu.
     **/
    protected _getMainContainerElement(): HTMLElement {
        return document.querySelector("tsc-app-settings-container");
    }

    /**
     * Funkce setřídí položky IUserRole nejprve podle aktivnosti a poté podle názvu. Pokud je zadáno undefined, vrací undefined.
     * @param unsortedItems Nesetříděné položky IUserRole.
     */
    protected _sortSettingItemsInfo(unsortedItems: IRole[] | undefined): IRole[] | undefined {
        if (unsortedItems) {
            return unsortedItems.sort((a, b) => (a.active === b.active) ? a.name.localeCompare(b.name) : (a.active < b.active) ? 1 : -1);
        } else {
            return undefined;
        }
    }

    public dispose(): void {
        this._okButton.removeEventListener("click", this._okButtonClickListener);
        this._closeButton.removeEventListener("click", this._closeButtonClickListener);
        this._rolesDropdown.removeEventListener("change", this._rolesDropDownChangeListener);
        this._rolesSection.selectionChanged.remove(this._rolesSelectionChangedListener);
    }
}

//bootstrapApp().then(() => {
registerCustomElement("tsc-app-settings-container", AppSettingsContainer);
//});
